"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
const materials = [{
  title: "Two-Color Laminate Acrylic (Engraving Stock)",
  description: "A cap layer of one color bonded over a contrasting core. Laser engraving removes the cap to reveal the core color — producing permanent, high-contrast text and graphics with no paint or infill required. Available in black/white, white/black, red/white, blue/white, yellow/black and other standard combinations. The industrial standard for nameplates and legend plates where phenolic is unavailable in the required color.",
  linkText: "View two-color acrylic specifications",
  linkUrl: "/materials/two-color-acrylic/",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/two_color_acrylic_engraving_stock.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/two_color_acrylic_engraving_stock.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/two_color_acrylic_engraving_stock.png-wvc-sizes",
    alt: "Two-color acrylic engraving stock — black cap engraved to reveal white core"
  }
}, {
  title: "Clear Cast Acrylic",
  description: 'Optically clear cast sheet, typically 1/8" to 1/4" thick for signs and 1/16" to 3/16" for precision-cut parts. Used for standoff signs, display panels, window inserts, and enclosure covers. Laser-cut edges are clean and slightly flame-polished. Engravable with surface-frosted results using CO₂ laser.',
  linkText: "",
  linkUrl: "",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/clear_cast_acrylic_standoff_sign.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/clear_cast_acrylic_standoff_sign.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/clear_cast_acrylic_standoff_sign.png-wvc-sizes",
    alt: "Clear cast acrylic sign panel cut to size for standoff mounting"
  }
}, {
  title: "Frosted and Colored Cast Acrylic",
  description: "Frosted white, matte black, translucent colors, and opaque colored cast sheet. Used for interior facility signage, room identification, and architectural sign panels. Can be engraved, cut to shape, and supplied with pre-drilled mounting holes or adhesive backing.",
  linkText: "",
  linkUrl: "",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/frosted_colored_acrylic_room_sign.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/frosted_colored_acrylic_room_sign.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/frosted_colored_acrylic_room_sign.png-wvc-sizes",
    alt: "Frosted acrylic room identification sign with laser-engraved text"
  }
}, {
  title: "Mirrored Acrylic",
  description: 'Silver and gold mirrored acrylic sheet for dimensional letters, lobby signs, and decorative applications. Laser-cut to shape from DXF or AI files. Available in standard thicknesses of 1/8" and 3/16". Pre-drilled for adhesive mounting or standoff hardware.',
  linkText: "",
  linkUrl: "",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mirrored_acrylic_lobby_letters.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mirrored_acrylic_lobby_letters.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mirrored_acrylic_lobby_letters.png-wvc-sizes",
    alt: "Laser-cut mirrored acrylic letters for lobby or reception signage"
  }
}];
const containerVariants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};
export default function MaterialShowcase() {
  return <section id="materialshowcase" className="bg-background py-20 md:py-28 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mx-auto max-w-3xl text-center mb-16">
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl mb-6">
            Acrylic Material Options
          </h2>
          <p className="text-lg leading-8 text-muted-foreground font-sans">
            Evermark Industrial stocks and machines cast acrylic sheet in the
            following configurations. Material selection depends on the visual
            requirement, the environment, and whether the sign or nameplate will
            be engraved, cut-only, or UV printed.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {materials.map((material, i) => <motion.div key={material.title} variants={itemVariants} data-index={i}>
              <Card className="h-full flex flex-col overflow-hidden border-border bg-card border-t-[3px] border-t-primary shadow-md hover:shadow-lg transition-shadow duration-300">
                <div className="w-full overflow-hidden">
                  <AspectRatio ratio={16 / 9}>
                    <img src={material.image.src} data-wvc-srcset={material.image.srcset} data-wvc-sizes={material.image.sizes} alt={material.image.alt} loading="lazy" className="object-cover w-full h-full transition-transform duration-500 hover:scale-105" />
                  </AspectRatio>
                </div>
                <CardHeader className="pb-4">
                  <CardTitle className="font-default text-xl text-card-foreground">
                    {material.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-base text-muted-foreground font-sans leading-relaxed">
                    {material.description}
                  </CardDescription>
                </CardContent>
                {material.linkUrl && material.linkText && <CardFooter className="pt-0 pb-6">
                    <Button variant="link" className="px-0 text-primary hover:text-primary/80 font-medium h-auto" asChild>
                      
                    </Button>
                  </CardFooter>}
              </Card>
            </motion.div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="flex justify-center">
          <Button variant="outline" size="lg" asChild>
            
          </Button>
        </motion.div>
      </div>
    </section>;
}